import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { packageService, type Package } from '@services/packageService';
import { CACHE_DURATION } from '../utils/cache';
import type { RootState } from '../index';

interface PackageState {
  packages: Package[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: PackageState = {
  packages: [],
  isLoading: false,
  isSaving: false,
  error: null,
  lastFetched: null,
};

export const fetchPackages = createAsyncThunk(
  'packages/fetchAll',
  async (options: { force?: boolean } | undefined, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { packages, lastFetched } = state.packages;

    if (
      !options?.force &&
      packages.length > 0 &&
      lastFetched &&
      Date.now() - lastFetched < CACHE_DURATION
    ) {
      return null;
    }

    try {
      const fetchedPackages = await packageService.getPackages();
      return fetchedPackages;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load packages';
      return rejectWithValue(message);
    }
  }
);

export const savePackage = createAsyncThunk(
  'packages/save',
  async (pkg: Package, { rejectWithValue }) => {
    try {
      const data = { name: pkg.name, items: pkg.items, isAddon: pkg.isAddon ?? false };
      return pkg.id.startsWith('tmp-')
        ? await packageService.createPackage(data)
        : await packageService.updatePackage(pkg.id, data);
    } catch (err: unknown) {
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to save package');
    }
  }
);

export const deletePackage = createAsyncThunk(
  'packages/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await packageService.deletePackage(id);
      return id;
    } catch (err: unknown) {
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to delete package');
    }
  }
);

const packageSlice = createSlice({
  name: 'packages',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchPackages.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPackages.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload !== null) {
          state.packages = action.payload;
          state.lastFetched = Date.now();
        }
      })
      .addCase(fetchPackages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(savePackage.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(savePackage.fulfilled, (state, action) => {
        state.isSaving = false;
        const index = state.packages.findIndex(pkg => pkg.id === action.payload.id);
        if (index < 0) state.packages.push(action.payload);
        else state.packages[index] = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(savePackage.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })
      .addCase(deletePackage.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(deletePackage.fulfilled, (state, action) => {
        state.isSaving = false;
        state.packages = state.packages.filter(pkg => pkg.id !== action.payload);
        state.lastFetched = Date.now();
      })
      .addCase(deletePackage.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      });
  },
});

export const selectPackages = (state: RootState) => state.packages.packages;
export const selectPackagesLoading = (state: RootState) => state.packages.isLoading;
export const selectPackagesError = (state: RootState) => state.packages.error;

export default packageSlice.reducer;
